import { useEffect, useRef } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { logToNewRelic } from '../../services/debug';
import { useStores } from '../../stores/useStores';
import Button from '../buttons/Button';
import { Loader } from '../Loader/Loader';

import classes from './GameIframe.module.scss';
import { useGameIframeEvents } from './hooks/useGameIframeEvents';

const GameIframe = observer(() => {
  const { gameStore, jackpotStore } = useStores();
  const { t } = useTranslation();
  const location = useLocation();

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const { isIframeLoading, onGameIframeError, retryIframeLoad } = useGameIframeEvents({ iframeRef });

  const { gameUiUrl, isGameUiError } = gameStore;

  const isInGamePath = location.pathname.startsWith('/games');

  /*
   * Tell the game document whether it is actually on screen.
   *
   * This iframe is never unmounted — it is hidden with `visibility: hidden` so it keeps its socket
   * and its state across a trip to the lobby (see the wrapper's class below). CSS visibility does not
   * move `document.visibilityState`, so from inside the iframe a game behind the lobby looks exactly
   * like a game in front of the player, and anything polling in there keeps polling for nobody —
   * Crown Jackpot's pot refresh, for one, duplicating the lobby's own 30s read.
   *
   * Queued when the iframe has not loaded yet (`resendOnIframeLoad`), so the first state is not the
   * one that gets dropped.
   */
  const isGameOnScreen = gameStore.showGameUi && isInGamePath;

  useEffect(() => {
    gameStore.sendSlotsGameUiMessage({ type: 'gameVisibility', payload: { visible: isGameOnScreen } }, true);
  }, [gameStore, isGameOnScreen]);

  /*
   * Leaving the game is when an unclaimed jackpot win gets offered again, and when the lobby's pots
   * are refreshed.
   *
   * The banner's `CLAIM` can simply be walked away from, and the lobby has no win surface of its own —
   * so the store checks for a PENDING win here and opens the ceremony in the jackpot dialog. Keyed on
   * the path rather than on `isGameOnScreen`, which also flips for a game-ui error or a launch that
   * has not resolved yet; neither of those is the player leaving.
   *
   * The refresh is here for the same reason: the poll skips ticks while the player is in a game, so
   * without it the lobby would paint a pot up to a full interval old. `refreshIfStale` carries its own
   * floor, so arriving seconds after a tick costs nothing.
   */
  useEffect(() => {
    if (isInGamePath) {
      return;
    }

    // `immediate`: a win from THIS game must not wait on the floor a lobby check just set.
    void jackpotStore.maybeOpenPendingWin({ immediate: true });
    void jackpotStore.refreshIfStale();
  }, [isInGamePath, jackpotStore]);

  useEffect(() => {
    if (!gameUiUrl) {
      void logToNewRelic(
        'GameIframe rendered null due to empty gameUiUrl',
        {
          gameUiFlag: gameStore.gameUiFlag,
          isGameUiError: gameStore.isGameUiError,
          currentGameId: gameStore.currentGame?.gameId,
          pathname: location.pathname,
          isInGamePath,
        },
        'warn',
      );
    }
  }, [
    gameUiUrl,
    gameStore.gameUiFlag,
    gameStore.isGameUiError,
    gameStore.currentGame?.gameId,
    location.pathname,
    isInGamePath,
  ]);

  if (!gameUiUrl) {
    return null;
  }

  if (isGameUiError) {
    return (
      <div
        className={classNames(classes.iframeWrapper, {
          [classes.hidden]: !isInGamePath,
        })}
      >
        <div className={classes.errorWrapper}>
          <p className={classes.errorMessage}>{t('game.gameWasUnableToLoad')}</p>
          <Button btnType="main" handleOnClick={retryIframeLoad}>
            {t('common.tryAgainUpperFIrstLetter')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(classes.iframeWrapper, {
        [classes.hidden]: !gameStore.showGameUi || !isInGamePath,
      })}
    >
      {(isIframeLoading || !gameStore.slotsGameUiIframe) && (
        <div className={classes.loaderWrapper}>
          <Loader fullScreen />
        </div>
      )}
      <iframe
        ref={iframeRef}
        allow="geolocation *; bluetooth *; accelerometer *; gyroscope *; web-share *; clipboard-write *; clipboard-read *"
        className={classes.iframe}
        data-testid="game-ui-iframe"
        height="100%"
        src={gameUiUrl}
        style={{ border: 'none' }}
        width="100%"
        onError={onGameIframeError}
      />
    </div>
  );
});

export default GameIframe;
